import React, { useContext, useState, useEffect, useMemo } from "react";
import useBreadcrumbs from "../../utils/hooks/useBreadcrumbs";
import { FaShoppingCart } from "react-icons/fa";
import Table from "../../UI/Table/Table";
import { PaginationType } from "../../Types/APITypes";
import Alert from "../../UI/Alert/Alert";
import { NotifyContext } from "../../context/notifyContext";
import { useNavigate } from "react-router-dom";
import OrderService from "../../services/api/OrderService";
import { OrderFilters, StripeOrder } from "../../Types/Dashboard";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

export default function OrderList() {
  useBreadcrumbs([
    {
      label: "Commandes",
      icon: <FaShoppingCart />,
      is_link: true,
      href: "/app/orders",
    },
    {
      label: "Liste",
      is_link: false,
    },
  ]);

  const notify = useContext(NotifyContext);
  const navigate = useNavigate();

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<StripeOrder[]>([]);
  const [pagination, setPagination] = useState<PaginationType>({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });
  const [filters, setFilters] = useState<OrderFilters>({
    page: 1,
    per_page: 15,
  });

  const fetchData = React.useCallback(
    (paginationData?: PaginationType) => {
      const paginationToUse = paginationData || {
        current_page: filters.page || 1,
        per_page: filters.per_page || 15,
        total: 0,
        last_page: 1,
      };
      setLoading(true);
      setError("");

      OrderService.getStripeOrders({
        ...filters,
        page: paginationToUse.current_page,
        per_page: paginationToUse.per_page,
      })
        .then((response) => {
          setOrders(response.data.orders);
          setPagination(response.data.meta);
        })
        .catch((err) => {
          setError(err?.message || "Erreur lors du chargement des commandes");
          notify(err?.message || "Erreur lors du chargement des commandes", "error");
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [filters, notify]
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "dd MMM yyyy, HH:mm", { locale: fr });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "delivered":
      case "completed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
      },
      {
        Header: "Client",
        Cell: (params: any) => {
          const { row } = params;
          const order: StripeOrder = row?.original;
          const displayName = order.customer?.name || order.customer_name || "Client anonyme";
          const displayEmail = order.customer?.email;
          return (
            <div>
              <p>{displayName}</p>
              {displayEmail && <p className="text-xs text-gray-500">{displayEmail}</p>}
            </div>
          );
        },
      },
      {
        Header: "Produits",
        Cell: (params: any) => {
          const { row } = params;
          const order: StripeOrder = row?.original;
          if (!order.products || order.products.length === 0) {
            return <p>-</p>;
          }
          const productNames = order.products.map((p) => p.name).join(", ");
          return (
            <p className="text-sm" title={productNames}>
              {productNames.length > 50 ? `${productNames.substring(0, 50)}...` : productNames}
            </p>
          );
        },
      },
      {
        Header: "Montant total",
        Cell: (params: any) => {
          const { row } = params;
          const order: StripeOrder = row?.original;
          return (
            <p className="font-medium">
              {order.amount.toLocaleString()} {order.currency}
            </p>
          );
        },
      },
      {
        Header: "Date",
        Cell: (params: any) => {
          const { row } = params;
          const order: StripeOrder = row?.original;
          return <p>{formatDate(order.created_at)}</p>;
        },
      },
      {
        Header: "Statut",
        Cell: (params: any) => {
          const { row } = params;
          const order: StripeOrder = row?.original;
          return (
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                order.status
              )}`}
            >
              {order.status}
            </span>
          );
        },
      },
      {
        Header: "Actions",
        Cell: (params: any) => {
          const { row } = params;
          const order: StripeOrder = row?.original;
          return (
            <button
              onClick={() => navigate(`/app/orders/${order.id}`)}
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              Voir détails
            </button>
          );
        },
      },
    ],
    [navigate]
  );

  return (
    <div className="flex flex-col overflow-auto">
      <div className="flex flex-col flex-1 p-6">
        {error && <Alert color="error">{error}</Alert>}

        <div className="mb-4 flex gap-4">
          <select
            value={filters.status || ""}
            onChange={(e) =>
              setFilters({ ...filters, status: e.target.value || undefined, page: 1 })
            }
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tous les statuts</option>
            <option value="PENDING">En attente</option>
            <option value="DELIVERED">Livré</option>
            <option value="CANCELLED">Annulé</option>
          </select>
        </div>

        <div className="bg-white shadow rounded overflow-hidden">
          <Table
            columns={columns as any}
            data={orders}
            fetchData={fetchData}
            loading={loading}
            pageCount={pagination.last_page || 1}
            total={pagination.total || 0}
          />
        </div>
      </div>
    </div>
  );
}
